<template>
  <section data-test-id="sms-page" class="sms-page">
    <div class="u-margin-top-6">
      <am2-heading
        type="h1"
        size="lg"
        title="SMS Admin"
        class="u-margin-bottom-4"
        :style="{ marginTop: '56px' }"
      />

      <am2-tabs
        :selected-tab-index="activeTabIndex"
        :items="tabItems"
        class="u-margin-bottom-8"
        @select="handleTabSelect"
      />

      <!-- SMS Customers tab -->
      <div v-if="activeTab === 'customers'">
        <ar-text
          class="u-margin-bottom-6"
          size="xs"
          text="All companies that have Twilio subaccounts are shown below"
        />
        <div
          v-if="isFetchingTwilioSMSSenderEnabledCompanies || twilioSMSSenderEnabledCompanies === null"
          class="loading-container"
        >
          <am2-loading-spinner size="48px" />
        </div>
        <ar-text
          v-else-if="twilioSMSSenderEnabledCompanies.length === 0"
          size="xs"
          text="No companies with Twilio subaccounts were found."
          :style="{ color: $arStyle.color.skyBlueGrey700 }"
        />
        <am2-table
          v-else
          :heads="tableHeadings"
          :rows="twilioSMSSenderEnabledCompanies"
          class="sms-companies-table"
          has-sticky-header
          enable-row-click
          has-control-section
          @rowClick="handleSMSRowClick"
        >
          <div slot="oid" slot-scope="{ data }" class="number-cell">
            <div class="body-container">
              <ar-text size="xs" :text="data.oid" />
            </div>
          </div>
          <div slot="emailAddress" slot-scope="{ data }" class="message-cell">
            <div class="body-container">
              <ar-text size="xs" :text="data.emailAddress" />
            </div>
          </div>
          <div slot="name" slot-scope="{ data }" class="message-cell">
            <div class="body-container">
              <ar-text size="xs" :text="data.name" />
            </div>
          </div>
          <div slot="country" slot-scope="{ data }" class="message-cell">
            <div class="body-container">
              <ar-text size="xs" :text="data.country" />
            </div>
          </div>
          <div slot="type" slot-scope="{ data }" class="message-cell">
            <div class="body-container">
              <ar-text size="xs" :text="data.type" />
            </div>
          </div>
        </am2-table>
      </div>

      <!-- SMS Report tab -->
      <div v-else-if="activeTab === 'report'" class="report-section">
        <ar-text
          class="u-margin-bottom-2"
          size="xs"
          text="Generate the SMS billing CSV for any month. The report will be emailed to the billing team in approximately 10–15 minutes."
        />

        <div class="report-controls u-margin-bottom-8">
          <ar-simple-select
            :items="monthOptions"
            :default-select-index="selectedMonthIndex"
            :style="{ width: '160px' }"
            @select="(item) => selectedMonth = item.value"
          />
          <ar-simple-select
            :items="yearOptions"
            :default-select-index="selectedYearIndex"
            :style="{ width: '110px', marginLeft: '12px' }"
            @select="(item) => selectedYear = item.value"
          />
          <ar-simple-button
            text="Generate Report"
            :loading="isGeneratingReport"
            :style="{ marginLeft: '16px' }"
            @click="handleDownloadReport"
          />
        </div>

        <am2-heading
          type="h2"
          size="sm"
          title="Report History"
          class="u-margin-bottom-4"
        />
        <div v-if="isFetchingSmsBillingReportTasks" class="loading-container">
          <am2-loading-spinner size="32px" />
        </div>
        <ar-text
          v-else-if="smsBillingReportTasks.length === 0"
          size="xs"
          text="No reports have been generated yet."
          :style="{ color: $arStyle.color.skyBlueGrey700 }"
        />
        <am2-table
          v-else
          :heads="reportTableHeadings"
          :rows="reportTableRows"
          has-sticky-header
        >
          <div slot="period" slot-scope="{ data }" class="message-cell">
            <ar-text size="xs" :text="data.period" />
          </div>
          <div slot="status" slot-scope="{ data }" class="message-cell">
            <am2-tag
              :text="data.statusLabel"
              :type="data.statusColor"
            />
          </div>
          <div slot="triggered" slot-scope="{ data }" class="message-cell">
            <ar-text size="xs" :text="data.triggered" />
          </div>
          <div slot="oid" slot-scope="{ data }" class="number-cell">
            <ar-text size="xs" :text="data.oid" />
          </div>
        </am2-table>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default {
  name: 'SmsAdmin',
  layout: 'default',

  data() {
    const now = new Date();
    // Default to previous month
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    return {
      activeTab: 'customers',
      selectedMonth: prevMonth,   // 0-indexed
      selectedYear: prevYear,
      isGeneratingReport: false,
    };
  },

  computed: {
    ...mapState({
      twilioSMSSenderEnabledCompanies: state => state.admin.twilioSMSSenderEnabledCompanies,
      isFetchingTwilioSMSSenderEnabledCompanies: state => state.admin.isFetchingTwilioSMSSenderEnabledCompanies,
      smsBillingReportTasks: state => state.admin.smsBillingReportTasks,
      isFetchingSmsBillingReportTasks: state => state.admin.isFetchingSmsBillingReportTasks,
    }),

    isAdminAccount() {
      return this.$store.getters['auth/isAdminAccount'];
    },

    tabItems() {
      return [
        { title: 'SMS Customers' },
        { title: 'SMS Report' },
      ];
    },

    activeTabIndex() {
      return this.activeTab === 'customers' ? 0 : 1;
    },

    monthOptions() {
      return MONTHS.map((name, idx) => ({ name, value: idx }));
    },

    selectedMonthIndex() {
      return this.selectedMonth;
    },

    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= currentYear - 3; y--) {
        years.push({ name: String(y), value: y });
      }
      return years;
    },

    selectedYearIndex() {
      const currentYear = new Date().getFullYear();
      return currentYear - this.selectedYear;
    },

    reportTableHeadings() {
      return [
        { name: 'Period',    key: 'period',    format: 'period',    align: 'left', width: 140 },
        { name: 'Status',    key: 'status',    format: 'status',    align: 'left', width: 120 },
        { name: 'Triggered', key: 'triggered', format: 'triggered', align: 'left', width: 200 },
        { name: 'Task ID',   key: 'oid',       format: 'oid',       align: 'left', width: 90  },
      ];
    },

    reportTableRows() {
      return this.smsBillingReportTasks.map(task => {
        const meta = task.meta || {};
        const month = meta.month ? String(meta.month).padStart(2, '0') : '??';
        const year  = meta.year  || '????';
        const statusColorMap = {
          completed:    'green',
          failed:       'red',
          'in-progress': 'purple',
          pending:      'grey',
        };
        return {
          oid:         task.oid,
          period:      `${month}/${year}`,
          statusLabel: task.status ? task.status.charAt(0).toUpperCase() + task.status.slice(1) : 'Unknown',
          statusColor: statusColorMap[task.status] || 'grey',
          triggered:   task.started ? new Date(task.started).toLocaleString() : '—',
        };
      });
    },

    tableHeadings() {
      return [
        { name: 'OID', key: 'oid', format: 'oid', align: 'left', width: 75, sortKey: 'oid' },
        { name: 'Company Name', key: 'name', format: 'name', align: 'left', width: 162, sortKey: 'name' },
        { name: 'Email', key: 'emailAddress', format: 'emailAddress', align: 'left', width: 233, sortKey: 'emailAddress' },
        { name: 'Region', key: 'country', format: 'country', align: 'left', width: 162, sortKey: 'country' },
        { name: 'Type', key: 'type', format: 'type', align: 'left', width: 190, sortKey: 'type' },
      ];
    },
  },

  mounted() {
    if (!this.isAdminAccount) {
      this.$router.push({ path: '/audience' });
      return;
    }
    // The endpoint returns all rows in one response (the action passes no
    // $top/$skip), so fetch once here. The previous v-infinite-scroll refire
    // looped forever on environments where the list is empty (EC dev1).
    this['admin/FETCH_SMS_ENABLED_COMPANIES']();
  },

  methods: {
    ...mapActions(['admin/FETCH_SMS_ENABLED_COMPANIES', 'admin/FETCH_SMS_BILLING_REPORT_TASKS']),

    handleTabSelect(item, idx) {
      this.activeTab = idx === 0 ? 'customers' : 'report';
      if (this.activeTab === 'report') {
        this['admin/FETCH_SMS_BILLING_REPORT_TASKS']();
      }
    },

    handleSMSRowClick(promoterAccount) {
      this.$router.push({
        path: `/admin/companies/${promoterAccount.oid}/edit/alpha-sms?from=sms`,
      });
    },

    async handleDownloadReport() {
      this.isGeneratingReport = true;
      try {
        // selectedMonth is 0-indexed; API expects 1-indexed
        const month = this.selectedMonth + 1;
        const year = this.selectedYear;

        await this.$axios.post('/sms-billing-report', { month, year });
        this.$arNotification.push({
          type: 'success',
          message: `SMS billing report for ${String(month).padStart(2, '0')}/${year} is being generated. You'll receive an email with the CSV when it's ready.`,
        });
        this['admin/FETCH_SMS_BILLING_REPORT_TASKS']();
      } catch (e) {
        this.$arNotification.push({
          type: 'error',
          message: 'Failed to trigger report. Please try again or contact engineering.',
        });
      } finally {
        this.isGeneratingReport = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.report-section {
  max-width: 800px;

  .report-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
  }
}

.message-cell, .number-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-cell {
  padding: 0 12px;
  overflow: hidden;
  justify-content: flex-start;
}

.number-cell {
  padding: 0 17px;
}

.loading-container {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}
</style>
